import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import api, { apiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '@/components/ui/popover';
import {
  Share2, Copy, RefreshCw, ExternalLink, Lock, Globe, Loader2, ShieldCheck,
} from 'lucide-react';
import { toast } from 'sonner';

export const SharePanel = ({ analysisId, shareToken, shareEnabled, onUpdate }) => {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const url = `${window.location.origin}/share/${shareToken}`;

  const toggle = async (enabled) => {
    setBusy(true);
    try {
      const { data } = await api.put(`/analyses/${analysisId}/share`, { enabled });
      onUpdate({ share_enabled: data.share_enabled, share_token: data.share_token });
      toast.success(enabled ? t('analysis.shareEnabledToast') : t('analysis.shareDisabledToast'));
    } catch (e) {
      toast.error(apiError(e, t('analysis.shareError')));
    } finally {
      setBusy(false);
    }
  };

  const regenerate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/analyses/${analysisId}/share/regenerate`);
      onUpdate({ share_enabled: data.share_enabled, share_token: data.share_token });
      toast.success(t('analysis.regeneratedToast'));
    } catch (e) {
      toast.error(apiError(e, t('analysis.shareError')));
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(url); toast.success(t('analysis.shareCopied')); }
    catch { toast.message(url); }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" className="border-white/20 bg-white/5 hover:bg-white/10" data-testid="analysis-share-btn">
          <Share2 className="w-4 h-4 mr-2" /> {t('analysis.share')}
          {shareEnabled && <span className="ml-2 w-2 h-2 rounded-full bg-[#2EE59D]" data-testid="share-active-dot" />}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-80 bg-[#12161B]/95 backdrop-blur-xl border-white/15 text-white"
        data-testid="share-panel"
      >
        <div className="flex items-center gap-2 mb-1">
          <Share2 className="w-4 h-4 text-[#48CAE4]" />
          <h4 className="font-semibold">{t('analysis.sharePanelTitle')}</h4>
        </div>

        <div className="flex items-center justify-between gap-3 rounded-xl bg-[#0A0A0A] border border-white/10 p-3 mt-3">
          <div className="flex items-center gap-2.5 min-w-0">
            {shareEnabled
              ? <Globe className="w-4 h-4 text-[#2EE59D] shrink-0" />
              : <Lock className="w-4 h-4 text-muted-foreground shrink-0" />}
            <div className="min-w-0">
              <div className="text-sm font-medium">{t('analysis.sharePublic')}</div>
              <div className="text-xs text-muted-foreground">
                {shareEnabled ? t('analysis.sharePublicHint') : t('analysis.sharePrivate')}
              </div>
            </div>
          </div>
          <Switch checked={shareEnabled} onCheckedChange={toggle} disabled={busy} data-testid="share-toggle" />
        </div>

        {shareEnabled && (
          <div className="mt-3 space-y-2" data-testid="share-link-block">
            <div className="flex items-center gap-2">
              <Input
                readOnly
                value={url}
                onFocus={(e) => e.target.select()}
                className="bg-[#0A0A0A] border-white/15 text-xs font-data h-9"
                data-testid="share-link-input"
              />
              <Button size="icon" onClick={copy} className="h-9 w-9 shrink-0 bg-[#00B4D8] hover:bg-[#48CAE4] text-black" data-testid="share-copy-btn" aria-label={t('analysis.copy')}>
                <Copy className="w-4 h-4" />
              </Button>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline" size="sm"
                onClick={regenerate} disabled={busy}
                className="flex-1 border-white/15 bg-white/5 hover:bg-white/10 h-9"
                data-testid="share-regenerate-btn"
              >
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
                {t('analysis.regenerate')}
              </Button>
              <Button asChild variant="outline" size="sm" className="border-white/15 bg-white/5 hover:bg-white/10 h-9" data-testid="share-open-btn">
                <a href={url} target="_blank" rel="noreferrer"><ExternalLink className="w-3.5 h-3.5 mr-1.5" /> {t('analysis.open')}</a>
              </Button>
            </div>
            <p className="text-[11px] text-muted-foreground flex items-start gap-1.5 pt-1">
              <ShieldCheck className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[#48CAE4]" /> {t('analysis.shareNoindex')}
            </p>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};
